import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { Network } from '@capacitor/network';
import { PromptService } from '../promtp-service/prompt.service';
import { QuestionnaireDiscontinuedError } from './questionnaire-discontinued.error';
import { QuestionnaireApiConfiguration } from 'src/app/api/proxy/questionnaire/questionnaire-api-configuration';
import {
  apiConsumerQuestionnaireListGet,
  apiConsumerQuestionnaireDetailPost,
  apiConsumerQuestionnaireHistoryGet,
  apiConsumerQuestionnaireSavePost,
  apiConsumerQuestionnaireSubmitPost,
  apiConsumerQuestionnaireQueueGet,
  apiConsumerQuestionnaireConsentAcceptPost,
  apiConsumerQuestionnaireConsentDeclinePost,
} from 'src/app/api/proxy/questionnaire/functions';
import {
  apiConsumerQuestionnaireReportsGet,
  apiConsumerQuestionnaireReportsMemberProfileQuestionnaireIdGet,
} from 'src/app/api/proxy/questionnaire/functions';
import {
  QuestionnaireListItemModel,
  QuestionnaireDetailModel,
  SaveQuestionnaireRequestModel,
  SaveQuestionnaireResponseModel,
  SubmitQuestionnaireRequestModel,
  SubmitQuestionnaireResponseModel,
  MemberQuestionnaireHistoryModel,
  QuestionnaireRequestListItemModel,
  QuestionnaireReportListItemModel,
  QuestionnaireReportModel,
  GenderType as QuestionnaireGenderType,
  AcceptQuestionnaireConsentRequestModel,
  DeclineQuestionnaireConsentRequestModel,
  QuestionnaireConsentResponseModel,
} from 'src/app/api/proxy/questionnaire/questionnaire-models';
import { EventService } from '../events/event.service';
import { BaseApiService } from '../base-api-service/base-api.service';
import { TokenService } from '../token/token.service';
import { SecureStorageKey } from '../storage/models/secure-storage-key.enum';
import { SecureStorageService } from '../storage/secure-storage.service';
import { MyProfileService } from '../my-profile-service/my-profile.service';
import { GenderType as ProfileGenderType, ProfileModel } from 'src/app/api/proxy/vitalscore/vitalscore-models';

@Injectable({
  providedIn: 'root',
})
export class QuestionnaireService extends BaseApiService {
  private readonly CACHE_TTL_MS = 30 * 60 * 1000;
  private readonly OBSERVATION_REFRESH_DELAY_MS = 3000;
  private NETWORK_TIMEOUT = 25000;
  private observationRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    protected _PromptService: PromptService,
    private http: HttpClient,
    private questionnaireCfg: QuestionnaireApiConfiguration,
    private _tokenService: TokenService,
    private _myProfileService: MyProfileService,
  ) {
    super(_PromptService);

    // Clear cache on logout only. Do not fetch on login here — ProfileDataRefresh /
    // home bootstrap load questionnaires after a valid token is available.
    EventService.userUpdated.subscribe((user) => {
      if (!user) {
        void this.clearRequestedQuestionnairesCache();
      }
    });

  }

  /** Called after fresh observation data is received from the API. */
  public refreshAfterObservationsReceived(): void {
    this.scheduleRequestedQuestionnairesRefreshAfterObservations();
  }

  /**
   * Get list of available questionnaires
   */
  public async getQuestionnaireList(): Promise<QuestionnaireListItemModel[] | null> {
    const obs = apiConsumerQuestionnaireListGet(this.http, this.questionnaireCfg.rootUrl, { activeOnly: true });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'getQuestionnaireList');
    const result = httpResponse ? httpResponse.body : null;

    if (result) {
      EventService.questionnaireListUpdated.emit(result);
      return result;
    }

    return null;
  }

  /**
   * Load active required questionnaire requests from cache when fresh; otherwise fetch and cache for 30 minutes.
   * Publishes the resolved queue via requestedQuestionnairesUpdated.
   */
  public async getRequestedQuestionnaires(
    forceRefresh = false,
  ): Promise<QuestionnaireRequestListItemModel[]> {
    let cached = await this.getCachedRequestedQuestionnaires();
    const lastRefresh = await SecureStorageService.get(SecureStorageKey.LastRequestedQuestionnairesRefresh);

    if (!forceRefresh && cached && cached.length > 0 && this.isCacheFresh(lastRefresh)) {
      return this.publishRequestedQuestionnairesUpdate(cached);
    }

    if (!forceRefresh && lastRefresh && !this.isCacheFresh(lastRefresh)) {
      await this.clearRequestedQuestionnairesStorage();
      cached = null;
    }

    if (!(await this.hasAccessToken())) {
      return this.publishRequestedQuestionnairesUpdate(cached ?? []);
    }

    if (!navigator.onLine) {
      return this.publishRequestedQuestionnairesUpdate(cached ?? []);
    }

    const fresh = await this.fetchRequestedQuestionnairesFromApi();
    if (fresh) {
      return await this.setCachedRequestedQuestionnaires(fresh);
    }

    return this.publishRequestedQuestionnairesUpdate(cached ?? []);
  }

  public async clearRequestedQuestionnairesCache(): Promise<void> {
    await this.clearRequestedQuestionnairesStorage();
    this.publishRequestedQuestionnairesUpdate([]);
  }

  /**
   * Get questionnaire detail by catalogue identifier.
   * Requests allowlisted HTML in localized text fields (`allowHtml: true`).
   * Throws {@link QuestionnaireDiscontinuedError} when the API returns 400 "Questionnaire not found".
   */
  public async getQuestionnaireDetail(questionnaireId: string): Promise<QuestionnaireDetailModel | null> {
    const profile = await this.ensureLatestProfileForQuestionnaire();
    const gender = this.mapProfileGender(profile?.gender);
    if (!profile?.dateOfBirth || !gender) {
      return null;
    }

    const obs = apiConsumerQuestionnaireDetailPost(this.http, this.questionnaireCfg.rootUrl, {
      body: {
        questionnaireId,
        dateOfBirth: profile.dateOfBirth,
        gender,
        allowHtml: true,
      },
    });

    const status = await Network.getStatus();
    if (!status.connected) {
      return null;
    }

    try {
      const httpResponse = await firstValueFrom(obs.pipe(timeout(this.NETWORK_TIMEOUT)));
      return httpResponse ? httpResponse.body : null;
    } catch (error) {
      if (this.isQuestionnaireNotFoundError(error)) {
        throw new QuestionnaireDiscontinuedError(questionnaireId);
      }
      await this._PromptService.showNetworkConnectionError(error, 'getQuestionnaireDetail');
      return null;
    }
  }

  /**
   * Remove a discontinued questionnaire from the cached requested-questionnaires list.
   */
  public async removeRequestedQuestionnaireFromCache(questionnaireId: string): Promise<void> {
    const cached = await this.getCachedRequestedQuestionnaires();
    const current = cached ?? [];
    const filtered = current.filter((item) => item.questionnaireId !== questionnaireId);
    if (filtered.length === current.length) {
      return;
    }
    await this.setCachedRequestedQuestionnaires(filtered);
  }

  /**
   * Get member's questionnaire submission history
   */
  public async getQuestionnaireHistory(questionnaireId: string): Promise<MemberQuestionnaireHistoryModel | null> {
    const obs = apiConsumerQuestionnaireHistoryGet(this.http, this.questionnaireCfg.rootUrl, {
      questionnaireId,
    });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'getQuestionnaireHistory');
    const result = httpResponse ? httpResponse.body : null;

    if (result) {
      EventService.questionnaireHistoryUpdated.emit(result);
      return result;
    }

    return null;
  }

  /**
   * Save questionnaire progress (partial submission)
   */
  public async saveProgress(request: SaveQuestionnaireRequestModel): Promise<SaveQuestionnaireResponseModel | null> {
    const obs = apiConsumerQuestionnaireSavePost(this.http, this.questionnaireCfg.rootUrl, { body: request });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'saveProgress');
    const result = httpResponse ? httpResponse.body : null;

    if (result) {
      await this.refreshRequestedQuestionnairesAfterMutation();
      return result;
    }

    return null;
  }

  /**
   * Submit questionnaire (final submission)
   */
  public async submitQuestionnaire(request: SubmitQuestionnaireRequestModel): Promise<SubmitQuestionnaireResponseModel | null> {
    const obs = apiConsumerQuestionnaireSubmitPost(this.http, this.questionnaireCfg.rootUrl, { body: request });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'submitQuestionnaire');
    const result = httpResponse ? httpResponse.body : null;

    if (result) {
      EventService.questionnaireSubmitted.emit(result);
      await this.refreshRequestedQuestionnairesAfterMutation();
      return result;
    }

    return null;
  }

  /**
   * Accept questionnaire-level consent before answering questions.
   */
  public async acceptConsent(
    request: AcceptQuestionnaireConsentRequestModel,
  ): Promise<QuestionnaireConsentResponseModel | null> {
    const obs = apiConsumerQuestionnaireConsentAcceptPost(this.http, this.questionnaireCfg.rootUrl, { body: request });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'acceptConsent');
    const result = httpResponse ? httpResponse.body : null;

    if (result) {
      await this.refreshRequestedQuestionnairesAfterMutation();
      return result;
    }

    return null;
  }

  /**
   * Decline questionnaire-level consent (empty attempt, no scoring).
   */
  public async declineConsent(
    request: DeclineQuestionnaireConsentRequestModel,
  ): Promise<QuestionnaireConsentResponseModel | null> {
    const obs = apiConsumerQuestionnaireConsentDeclinePost(this.http, this.questionnaireCfg.rootUrl, { body: request });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'declineConsent');
    const result = httpResponse ? httpResponse.body : null;

    if (result) {
      await this.refreshRequestedQuestionnairesAfterMutation();
      return result;
    }

    return null;
  }

  /**
   * Lists completed questionnaire reports for the current member.
   */
  public async getQuestionnaireReports(): Promise<QuestionnaireReportListItemModel[] | null> {
    const obs = apiConsumerQuestionnaireReportsGet(this.http, this.questionnaireCfg.rootUrl);
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'getQuestionnaireReports');
    return httpResponse ? httpResponse.body : null;
  }

  /**
   * Gets a single questionnaire report by member profile questionnaire id.
   */
  public async getQuestionnaireReport(
    memberProfileQuestionnaireId: string,
    allowHtml: boolean = true,
  ): Promise<QuestionnaireReportModel | null> {
    const obs = apiConsumerQuestionnaireReportsMemberProfileQuestionnaireIdGet(
      this.http,
      this.questionnaireCfg.rootUrl,
      { memberProfileQuestionnaireId, allowHtml },
    );
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'getQuestionnaireReport');
    return httpResponse ? httpResponse.body : null;
  }

  private async ensureLatestProfileForQuestionnaire(): Promise<ProfileModel | null> {
    return await this._myProfileService.GetProfile(true, false);
  }

  public mapProfileGender(
    gender: ProfileGenderType | string | null | undefined,
  ): QuestionnaireGenderType | null {
    if (gender === ProfileGenderType.Male || gender === 'Male') {
      return QuestionnaireGenderType.Male;
    }

    if (gender === ProfileGenderType.Female || gender === 'Female') {
      return QuestionnaireGenderType.Female;
    }

    return null;
  }

  private publishRequestedQuestionnairesUpdate(
    items: QuestionnaireRequestListItemModel[],
  ): QuestionnaireRequestListItemModel[] {
    const normalized = items ?? [];
    EventService.requestedQuestionnairesUpdated.emit(normalized);
    return normalized;
  }

  private async fetchRequestedQuestionnairesFromApi(): Promise<QuestionnaireRequestListItemModel[] | null> {
    const obs = apiConsumerQuestionnaireQueueGet(this.http, this.questionnaireCfg.rootUrl);
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'getRequestedQuestionnaires');
    return httpResponse?.body ?? null;
  }

  private async getCachedRequestedQuestionnaires(): Promise<QuestionnaireRequestListItemModel[] | null> {
    const cached = await SecureStorageService.get(SecureStorageKey.RequestedQuestionnaires);
    if (!cached) {
      return null;
    }

    try {
      return JSON.parse(cached) as QuestionnaireRequestListItemModel[];
    } catch {
      return null;
    }
  }

  private async setCachedRequestedQuestionnaires(
    items: QuestionnaireRequestListItemModel[],
  ): Promise<QuestionnaireRequestListItemModel[]> {
    const normalized = items ?? [];
    if (normalized.length > 0) {
      await SecureStorageService.set(SecureStorageKey.RequestedQuestionnaires, JSON.stringify(normalized));
      await SecureStorageService.set(SecureStorageKey.LastRequestedQuestionnairesRefresh, Date.now().toString());
    } else {
      await SecureStorageService.remove(SecureStorageKey.RequestedQuestionnaires);
      await SecureStorageService.remove(SecureStorageKey.LastRequestedQuestionnairesRefresh);
    }
    return this.publishRequestedQuestionnairesUpdate(normalized);
  }

  private isCacheFresh(lastRefresh: string | null): boolean {
    if (!lastRefresh) {
      return false;
    }

    const parsed = parseInt(lastRefresh, 10);
    if (Number.isNaN(parsed)) {
      return false;
    }

    return Date.now() - parsed < this.CACHE_TTL_MS;
  }

  private async hasAccessToken(): Promise<boolean> {
    const token = await this._tokenService.getTokenAsync();
    return !!token;
  }

  private async refreshRequestedQuestionnairesAfterMutation(): Promise<void> {
    await this.clearRequestedQuestionnairesStorage();
    await this.getRequestedQuestionnaires(true);
  }

  private scheduleRequestedQuestionnairesRefreshAfterObservations(): void {
    if (this.observationRefreshTimer) {
      clearTimeout(this.observationRefreshTimer);
    }

    void this.clearRequestedQuestionnairesStorage();

    this.observationRefreshTimer = setTimeout(() => {
      this.observationRefreshTimer = null;
      void this.getRequestedQuestionnaires(true);
    }, this.OBSERVATION_REFRESH_DELAY_MS);
  }

  private async clearRequestedQuestionnairesStorage(): Promise<void> {
    await SecureStorageService.remove(SecureStorageKey.RequestedQuestionnaires);
    await SecureStorageService.remove(SecureStorageKey.LastRequestedQuestionnairesRefresh);
  }

  private isQuestionnaireNotFoundError(error: unknown): boolean {
    const httpError = error as { status?: number };
    if (httpError?.status !== 400) {
      return false;
    }

    const message = this._PromptService.tryGetServerErrorMessage(error);
    return message?.toLowerCase() === 'questionnaire not found';
  }
}
